import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useRoute } from "wouter";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Facebook,
  Flag,
  Globe,
  HelpCircle,
  Instagram,
  MapPin,
  Music2,
  Pencil as PencilIcon,
  RefreshCw,
  Share2,
  Sparkles,
  Tag,
  UserCircle2,
  Users,
  X,
} from "lucide-react";
import { useEventPublic, type PublicEventResponse } from "../lib/api/useEventPublic";
import { useSimilarEvents } from "../lib/api/useEventsList";
import { CarouselRail } from "../components/EventCarouselRail";
import { getLinkDisplay, type LinkType } from "@shared/eventLinks";

const LINK_CONFIGS: Record<LinkType, { label: string; bg: string; Icon: React.ComponentType<{ className?: string }> }> = {
  web:       { label: "Website",   bg: "bg-neutral-500",                                Icon: Globe },
  instagram: { label: "Instagram", bg: "bg-gradient-to-br from-purple-500 to-pink-500", Icon: Instagram },
  facebook:  { label: "Facebook",  bg: "bg-blue-600",                                   Icon: Facebook },
  tiktok:    { label: "TikTok",    bg: "bg-neutral-900",                                Icon: Music2 },
  venmo:     { label: "Venmo",     bg: "bg-sky-500",                                    Icon: DollarSign },
};
import { BuddyAvatar } from "../components/BuddyAvatar";
import { EventPoster } from "../components/EventPoster";
import { EventActionBar } from "../components/EventActionBar";
import { EventRsvpSheet } from "../components/EventRsvpSheet";
import { ReportEventModal } from "../components/ReportEventModal";
import {
  useEventBuddies,
  useBuddyRequestDetail,
  useUpdateBuddyApplicationStatus,
  type BuddyRequestItem,
  type OwnerBuddyApplication,
} from "../lib/api/useEventBuddies";
import { useToast } from "../components/ux/Toast";
import { CreateKikiModal } from "../components/CreateKikiModal";
import { useProfile, useIsSubscriber } from "../lib/api/useProfile";
import { SubscribeInterstitial } from "../components/SubscribeInterstitial";
import {
  useCancelRsvp,
  useEventInterested,
  useEventRsvps,
  useMarkInterested,
  useRsvpEvent,
} from "../lib/api/useEventRsvp";
import { useInterestedCelebration } from "../lib/interestedCelebration";
import { useAuth } from "../lib/platform";
import { parseUTCDate, formatTimeToDisplay } from "../utils/dateUtils";
import { formatPrice } from "../utils/price";
import { sanitizeRichText } from "../utils/sanitizeHtml";
import { getEventType } from "@shared/eventTypes";
import { formatNameWithInitial } from "@shared/formatName";
import Logo from "@/components/Logo";
import { Loader } from "@/components/Loader";
import { ProfileImage } from "../components/ProfileImage";
import { DateBadge } from "../components/ux/DateBadge";
import { ImageLightbox } from "../components/ux/ImageLightbox";
import { ShareEventModal } from "../components/ShareEventModal";
import { KikiHelpModal } from "../components/KikiHelpModal";
import { OnboardingProvider, useOnboardingGate } from "../components/OnboardingContext";

const RSVP_INTENT_KEY = "pendingEventRsvpSlug";


function audienceLine(filter: PublicEventResponse["audienceFilter"]): string | null {
  if (!filter) return null;
  const parts: string[] = [];
  if (filter.genders?.length) parts.push(filter.genders.join("/"));
  if (filter.orientations?.length) parts.push(filter.orientations.join("/"));
  if (filter.ageMin != null && filter.ageMax != null) parts.push(`${filter.ageMin}–${filter.ageMax}`);
  else if (filter.ageMin != null) parts.push(`${filter.ageMin}+`);
  else if (filter.ageMax != null) parts.push(`under ${filter.ageMax + 1}`);
  return parts.length ? parts.join(", ") : null;
}

function locationFallback(venue: PublicEventResponse["venue"]): string | null {
  if (!venue) return null;
  const bits = [venue.neighborhood, venue.city].filter(Boolean) as string[];
  return bits.length ? bits.join(", ") : null;
}

export default function EventPublic() {
  // Provider so signed-out gating opens the onboarding explainer (what Outclose
  // is + sign up), falling back to plain sign-in only outside this tree.
  return (
    <OnboardingProvider>
      <EventPublicContent />
    </OnboardingProvider>
  );
}

function EventPublicContent() {
  const [, params] = useRoute("/events/:slug");
  const slug = params?.slug || null;
  const [, navigate] = useLocation();
  const { data, isLoading, error } = useEventPublic(slug);
  const [buddyModalOpen, setBuddyModalOpen] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [editingBuddy, setEditingBuddy] = useState<BuddyRequestItem | null>(null);
  const [rsvpSheetOpen, setRsvpSheetOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Bumped after each lightbox close-animation completes. Used as a `key`
  // on the thumb's motion.div so framer-motion's projection cache for the
  // shared `event-cover` layoutId resets between cycles — otherwise the
  // close-morph snaps/flickers on the second and subsequent closes.
  const [coverThumbKey, setCoverThumbKey] = useState(0);
  const [celebrate, setCelebrate] = useState(false);
  const [kikiHelpOpen, setKikiHelpOpen] = useState(false);
  const prevIsRsvpedRef = useRef<boolean | undefined>(undefined);
  const buddies = useEventBuddies(slug);
  const buddyItems = buddies.data?.success ? buddies.data.data ?? [] : [];
  const myKiki = buddyItems.find((b) => b.mine) ?? null;

  const event = data?.success ? data.data : null;

  // Check ownership — show the edit button only when the signed-in user
  // matches the event's host. useProfile is harmless when unauthenticated
  // (returns no data); skipping the request entirely would require auth
  // signal, which we don't need here.
  const { data: profileData } = useProfile();
  const currentUserId = profileData?.success
    ? (profileData.data?.user?.id as string | undefined) ?? null
    : null;
  const isOwner = !!event?.host && !!currentUserId && event.host.id === currentUserId;
  const { isSignedIn, openSignIn } = useAuth();
  const onboarding = useOnboardingGate();
  // Signed-out gating → the onboarding explainer (with return-to-this-event),
  // falling back to the plain sign-in dialog if no provider is mounted.
  const gateSignIn = () =>
    onboarding ? onboarding.openOnboarding(slug ? `/events/${slug}` : undefined) : openSignIn();
  const isSubscriber = useIsSubscriber();

  // Starting a new Kiki is members-only: signed-out → sign-in, signed-in
  // non-subscriber → membership interstitial, subscriber → the create form.
  const startKiki = () => {
    setEditingBuddy(null);
    if (!isSignedIn) {
      gateSignIn();
      return;
    }
    if (!isSubscriber) {
      setSubscribeOpen(true);
      return;
    }
    setBuddyModalOpen(true);
  };
  // Back always returns to the events index, not browser history.
  const goBack = () => navigate("/events");
  const { openSheet: openInterestedSheet } = useInterestedCelebration();
  const rsvp = useRsvpEvent(slug ?? "");
  const cancelRsvp = useCancelRsvp(slug ?? "");
  const markInterested = useMarkInterested(slug ?? "");
  const rsvps = useEventRsvps(slug);
  const rsvpItems = rsvps.data?.success ? rsvps.data.data ?? [] : [];
  const interested = useEventInterested(slug);
  const interestedItems = interested.data?.success ? interested.data.data ?? [] : [];

  useEffect(() => {
    if (!event) {
      document.title = "Event - Outclose";
      return;
    }
    document.title = `${event.name ?? "Event"} — Outclose`;
    const robotsContent = event.visibility === "unlisted" ? "noindex,nofollow" : "index,follow";
    let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", robotsContent);
  }, [event]);

  const parsedDate = useMemo(() => event ? parseUTCDate(event.date) : null, [event]);

  const timeLabel = useMemo(() => {
    if (!event) return null;
    const start = formatTimeToDisplay(event.time);
    if (!event.endTime) return start;
    const end = formatTimeToDisplay(event.endTime);
    return `${start} – ${end}`;
  }, [event]);

  const audience = audienceLine(event?.audienceFilter ?? null);
  const fallbackLocation = locationFallback(event?.venue ?? null);

  // Auto-open the sheet (with celebration) the moment the user RSVPs.
  useEffect(() => {
    const curr = event?.isRsvped;
    if (prevIsRsvpedRef.current === false && curr === true) {
      setCelebrate(true);
      setRsvpSheetOpen(true);
    }
    if (curr !== undefined) prevIsRsvpedRef.current = curr;
  }, [event?.isRsvped]);

  const handleRsvpClick = () => {
    if (!slug) return;
    if (isSignedIn) {
      if (event?.isRsvped) {
        setCelebrate(false);
        setRsvpSheetOpen(true);
      } else {
        rsvp.mutate();
      }
      return;
    }
    localStorage.setItem(RSVP_INTENT_KEY, slug);
    gateSignIn();
  };

  const handleInterestedClick = () => {
    if (!slug) return;
    if (isSignedIn) {
      if (event?.isInterested) {
        openInterestedSheet(slug, false);
      } else {
        openInterestedSheet(slug, true);
        markInterested.mutate();
      }
      return;
    }
    gateSignIn();
  };

  // After sign-in, auto-fire the RSVP if this page is the one the user
  // originally clicked from. Runs once when the user becomes signed in.
  useEffect(() => {
    if (!slug || !isSignedIn) return;
    const pending = localStorage.getItem(RSVP_INTENT_KEY);
    if (pending !== slug) return;
    localStorage.removeItem(RSVP_INTENT_KEY);
    rsvp.mutate();
    // rsvp.mutate is stable enough; only re-run when sign-in state flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isSignedIn]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="max-w-md mx-auto p-6 text-center min-h-[60vh] flex flex-col justify-center">
        <h1 className="text-3xl font-bold mb-2">Event not found</h1>
        <p className="text-neutral-500 dark:text-neutral-400">
          The event you're looking for doesn't exist or has been removed.
        </p>
      </div>
    );
  }

  const showRsvp = !event.isPast && (event.spotsLeft == null || event.spotsLeft > 0);
  const isFull = !event.isPast && event.spotsLeft === 0;

  const hasSecondary =
    event.types.length > 0 || event.capacity != null || event.priceCents != null || audience != null;

  return (
    <div className="min-h-screen pb-52 sm:pb-28 md:pb-20 pt-4">
      <Logo withIcon={true} className="md:hidden justify-center pt-4 mb-4" />

      <motion.main
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="max-w-2xl mx-auto px-4 sm:px-6 pt-2 space-y-5"
      >
        <div className="flex items-center justify-between -ml-1">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400 hover:text-primary-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            All events
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShareModalOpen(true)}
              className="inline-flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400 hover:text-primary-500 transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
            {isOwner && (
              <Link
                href={`/events/${slug}/edit`}
                className="inline-flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400 hover:text-primary-500 transition-colors"
              >
                <PencilIcon className="w-4 h-4" />
                Edit
              </Link>
            )}
          </div>
        </div>

        {/* Hero + description card — cover image with title overlay at the
            top, prose description below, all one visual unit. */}
        <div className="card !p-0 overflow-hidden">
          {/* Square cover — no overlays. The title and RSVP button live in
              the row underneath so the image stays unobstructed. */}
          <div className="relative aspect-square bg-neutral-900">
            {event.coverImageUrl ? (
              <motion.div
                key={coverThumbKey}
                layoutId="event-cover"
                className="absolute inset-0 cursor-zoom-in overflow-hidden"
                onClick={() => setLightboxOpen(true)}
                transition={{ type: "spring", stiffness: 340, damping: 32 }}
              >
                <img
                  src={event.coverImageUrl}
                  alt={event.name ?? "Event"}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ) : (
              <EventPoster
                name={event.name ?? "Untitled event"}
                label={event.posterLabel}
                types={(event.types ?? []) as string[]}
              />
            )}
          </div>
          <ImageLightbox
            src={event.coverImageUrl ?? ""}
            alt={event.name ?? "Event"}
            layoutId="event-cover"
            isOpen={lightboxOpen && !!event.coverImageUrl}
            onClose={() => setLightboxOpen(false)}
            onExitComplete={() => setCoverThumbKey((k) => k + 1)}
          />

          {/* Title row — name (and optional past-event chip) on the left,
              action bar on the right (desktop only). */}
          <div className="px-5 sm:px-6 pt-5 pb-3 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              {event.isPast && (
                <div className="mb-2">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-base-200 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 text-xs">
                    This event has happened
                  </span>
                </div>
              )}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-tight">
                {event.name ?? "Untitled event"}
              </h1>
            </div>
            {!event.isPast && (
              <div className="hidden sm:block shrink-0">
                <EventActionBar
                  variant="inline"
                  isRsvped={event.isRsvped || cancelRsvp.isPending}
                  isInterested={event.isInterested}
                  showRsvp={showRsvp}
                  isFull={isFull}
                  interestedPending={markInterested.isPending}
                  onRsvp={handleRsvpClick}
                  onInterested={handleInterestedClick}
                />
              </div>
            )}
          </div>

          {/* Date + host — calendar badge */}
          {parsedDate && (
            <div className="px-5 sm:px-6 py-4 border-b border-black/10 dark:border-white/10 flex items-center gap-4">
              <DateBadge date={event.date} size="xl" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <p className="text-sm font-semibold leading-none">
                  {parsedDate.weekday}
                  {timeLabel && (
                    <span className="font-normal text-neutral-500 dark:text-neutral-400"> · {timeLabel}</span>
                  )}
                </p>
                {event.host && <HostCard host={event.host} />}
              </div>
            </div>
          )}

          {/* Interested strip — face stack (blurred until signed in). The whole
              strip is clickable: signed-in users go to the interested list,
              signed-out users hit the sign-in gate. */}
          {interestedItems.length > 0 && (() => {
            const faces = (
              <>
                <div
                  className={`flex items-center -space-x-1.5 min-w-0 ${
                    isSignedIn ? "" : "blur-[5px] select-none"
                  }`}
                  aria-hidden={isSignedIn ? undefined : true}
                >
                  {interestedItems.slice(0, 8).map((r) => {
                    const name = r.lastName
                      ? `${r.firstName} ${r.lastName.charAt(0).toUpperCase()}.`
                      : r.firstName;
                    return (
                      <div
                        key={r.id}
                        title={isSignedIn ? name : undefined}
                        className="ring-2 ring-white dark:ring-neutral-900 rounded-full"
                      >
                        <ProfileImage
                          size="sm"
                          user={{
                            firstName: r.firstName,
                            lastName: r.lastName,
                            profileImageUrl: r.profileImageUrl,
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
                {isSignedIn ? (
                  <span className="text-xs text-neutral-400 dark:text-neutral-500 group-hover:text-primary-500 transition-colors">
                    {interestedItems.length} interested
                  </span>
                ) : (
                  <span className="text-xs font-medium text-primary-500 group-hover:text-primary-600 transition-colors">
                    Sign in to see who's interested
                  </span>
                )}
              </>
            );
            const stripClass =
              "group w-full text-left px-5 sm:px-6 py-3 border-b border-black/10 dark:border-white/10 flex items-center gap-3 hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors";
            return isSignedIn ? (
              <Link href={`/events/${slug}/interested`} className={stripClass}>
                {faces}
              </Link>
            ) : (
              <button type="button" onClick={() => gateSignIn()} className={stripClass}>
                {faces}
              </button>
            );
          })()}

          {/* Kiki strip */}
          <div className="px-5 sm:px-6 pt-4 pb-5 border-b border-black/10 dark:border-white/10">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                Let's have a kiki
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setKikiHelpOpen(true); }}
                className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                aria-label="What is a kiki?"
              >
                <HelpCircle className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => buddies.refetch()}
                className="ml-auto text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
                aria-label="Refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${buddies.isFetching ? "animate-spin" : ""}`} />
              </button>
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
              Connect with others and go together
            </p>

            <div className="flex items-start gap-4 overflow-x-auto pb-1 -mx-1 px-1">
              {/* Add button — always first */}
              <button
                type="button"
                onClick={startKiki}
                className="shrink-0 flex flex-col items-center gap-1.5 group"
              >
                <div className="h-14 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full border-2 border-dashed border-primary-400 dark:border-primary-500 bg-primary-500/5 group-hover:bg-primary-500/15 flex items-center justify-center transition-colors">
                    <span className="text-2xl font-light text-primary-500 leading-none">+</span>
                  </div>
                </div>
                <span className="text-[11px] text-primary-500 font-medium w-16 text-center leading-tight truncate">
                  Create
                </span>
              </button>

              {/* Existing kikis */}
              {buddyItems.map((b) => (
                <BuddyAvatar
                  key={b.id}
                  buddy={b}
                  onClick={() => navigate(`/kiki/${b.id}`)}
                />
              ))}
            </div>
          </div>

          {/* Your Kiki management panel */}
          {myKiki && slug && (
            <MyKikiPanel
              slug={slug}
              myKiki={myKiki}
              onEdit={() => { setEditingBuddy(myKiki); setBuddyModalOpen(true); }}
            />
          )}

          {/* Tags + pricing chips */}
          {hasSecondary && (
            <>
              <div className="border-t border-black/10 dark:border-white/10" />
              <div className="px-5 sm:px-6 py-4">
                <div className="flex flex-wrap gap-2">
                  {event.types.map((slug) => {
                    const t = getEventType(slug);
                    if (!t) return null;
                    return (
                      <Link
                        key={slug}
                        href={`/events?types=${encodeURIComponent(slug)}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-base-200 dark:bg-neutral-800 hover:bg-base-300 dark:hover:bg-neutral-700 text-sm font-medium transition-colors"
                      >
                        <span>{t.emoji}</span>
                        <span>{t.label}</span>
                      </Link>
                    );
                  })}
                  {event.priceCents != null && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-base-200 dark:bg-neutral-800 text-sm">
                      <Tag className="w-3.5 h-3.5 text-neutral-400" />
                      <span className="font-medium">{formatPrice(event.priceCents)}</span>
                      {event.priceCents > 0 && (
                        <span className="text-neutral-500 dark:text-neutral-400">/ guest</span>
                      )}
                    </span>
                  )}
                  {event.capacity != null && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-base-200 dark:bg-neutral-800 text-sm">
                      <Users className="w-3.5 h-3.5 text-neutral-400" />
                      <span className="font-medium">{event.attendeeCount} / {event.capacity}</span>
                      {event.spotsLeft != null && event.spotsLeft > 0 && !event.isPast && (
                        <span className="text-neutral-400 dark:text-neutral-500"> · {event.spotsLeft} left</span>
                      )}
                    </span>
                  )}
                  {audience && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-base-200 dark:bg-neutral-800 text-sm">
                      <Sparkles className="w-3.5 h-3.5 text-neutral-400" />
                      <span className="font-medium">{audience}</span>
                    </span>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Body — description */}
          {event.description && (
            <>
              <div className="p-5 sm:p-6 pt-0">
                <div
                  className="prose prose-sm sm:prose-base dark:prose-invert max-w-none leading-relaxed whitespace-break-spaces"
                  dangerouslySetInnerHTML={{ __html: sanitizeRichText(event.description) }}
                />
              </div>
            </>
          )}

          {/* Links */}
          {(event.links ?? []).length > 0 && (
            <>
              <div className="border-t border-black/10 dark:border-white/10" />
              <div className="px-5 sm:px-6 py-4 flex flex-col gap-3">
                {(event.links ?? []).map((link, i) => {
                  const lc = LINK_CONFIGS[link.type] ?? LINK_CONFIGS.web;
                  const display = getLinkDisplay(link);
                  return (
                    <a
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 group"
                    >
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${lc.bg}`}>
                        <lc.Icon className="w-3 h-3 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm">{lc.label}</div>
                        <div className="text-sm text-neutral-500 dark:text-neutral-400 truncate group-hover:text-primary-500 transition-colors">{display}</div>
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-neutral-400 shrink-0 mt-0.5" />
                    </a>
                  );
                })}
              </div>
            </>
          )}

          {/* Venue section */}
          {event.venue && (
            <>
              <div className="border-t border-black/10 dark:border-white/10" />
              {event.venue.address ? (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    `${event.venue.name}, ${event.venue.address}`,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-5 sm:p-6 flex items-start gap-3 group"
                >
                  <MapPin className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{event.venue.name}</div>
                    <div className="text-sm text-neutral-500 dark:text-neutral-400 group-hover:text-primary-500 transition-colors">
                      {event.venue.address}
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-neutral-400 shrink-0 mt-0.5" />
                </a>
              ) : (
                <div className="p-5 sm:p-6 flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{event.venue.name}</div>
                    {fallbackLocation && (
                      <div className="text-sm text-neutral-500 dark:text-neutral-400">{fallbackLocation}</div>
                    )}
                    {!event.isPast && event.addressRevealPolicy !== "immediately" && (
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 italic mt-1">
                        Exact address shared after RSVP
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Report link */}
          {!isOwner && (
            <>
              <div className="border-t border-black/10 dark:border-white/10" />
              <div className="px-5 sm:px-6 py-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    if (!isSignedIn) { gateSignIn(); return; }
                    setReportModalOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-rose-500 dark:text-neutral-500 dark:hover:text-rose-400 transition-colors"
                >
                  <Flag className="w-3 h-3" />
                  Report event
                </button>
              </div>
            </>
          )}
        </div>

        {isFull && (
          <div className="text-center px-4 py-3 rounded-full bg-neutral-200 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 text-sm">
            Event is full
          </div>
        )}

        {!event.host && (
          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center px-6 leading-relaxed">
            Outclose is not the organizer of this event. Details are sourced from public listings and kept up to date as best we can. Confirm specifics with the organizer before you go.
            <br />
            Is this your event?{" "}
            <a href="mailto:team@outclose.com?subject=Claim my event page" className="underline hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors">
              Claim your page.
            </a>
          </p>
        )}

        <SimilarEvents slug={slug} />
      </motion.main>

      {!event.isPast && (
        <div className="sm:hidden">
          <EventActionBar
            isRsvped={event.isRsvped || cancelRsvp.isPending}
            isInterested={event.isInterested}
            showRsvp={showRsvp}
            isFull={isFull}
            interestedPending={markInterested.isPending}
            onRsvp={handleRsvpClick}
            onInterested={handleInterestedClick}
          />
        </div>
      )}

      <EventRsvpSheet
        isOpen={rsvpSheetOpen}
        celebrate={celebrate}
        onClose={() => setRsvpSheetOpen(false)}
        onLeave={() => {
          cancelRsvp.mutate(undefined, {
            onSettled: () => setRsvpSheetOpen(false),
          });
        }}
        onTeamup={() => { setRsvpSheetOpen(false); startKiki(); }}
        onBuddyClick={(id) => { setRsvpSheetOpen(false); navigate(`/kiki/${id}`); }}
        rsvpItems={rsvpItems}
        buddyItems={buddyItems}
      />

      <CreateKikiModal
        isOpen={buddyModalOpen}
        onClose={() => {
          setBuddyModalOpen(false);
          setEditingBuddy(null);
        }}
        event={{
          slug: slug ?? "",
          name: event.name ?? "Event",
          date: event.date,
          time: event.time,
          coverImageUrl: event.coverImageUrl,
          venueName: event.venue?.name ?? null,
          venueCity: event.venue?.city ?? null,
          types: (event.types ?? []) as string[],
          posterLabel: event.posterLabel,
        }}
        editing={editingBuddy ? { id: editingBuddy.id, note: editingBuddy.note } : null}
      />

      <SubscribeInterstitial
        isOpen={subscribeOpen}
        onClose={() => setSubscribeOpen(false)}
        title="Team up on this event"
        description="Join Outclose to start a Kiki and meet people going to this event."
        onSubscribed={() => setBuddyModalOpen(true)}
      />

      <ReportEventModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        slug={slug ?? ""}
      />

      {event && (
        <ShareEventModal
          isOpen={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
          event={event}
        />
      )}

      <KikiHelpModal isOpen={kikiHelpOpen} onClose={() => setKikiHelpOpen(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Your Kiki — owner management panel
// ---------------------------------------------------------------------------

function MyKikiPanel({
  slug,
  myKiki,
  onEdit,
}: {
  slug: string;
  myKiki: BuddyRequestItem;
  onEdit: () => void;
}) {
  const { data } = useBuddyRequestDetail(slug, myKiki.id);
  const detail = data?.success ? data.data : null;
  const [openApplicantId, setOpenApplicantId] = useState<string | null>(null);
  const [showDeclined, setShowDeclined] = useState(false);

  const apps = detail?.ownerApplications ?? [];
  const accepted = apps.filter((a) => a.status === "accepted");
  const pending = apps.filter((a) => a.status === "pending");
  const declined = apps.filter((a) => a.status === "declined");

  const toggle = (id: string) =>
    setOpenApplicantId((cur) => (cur === id ? null : id));

  const bubbleRow = (list: typeof apps, dim = false) => (
    <div className={`flex gap-4 overflow-x-auto pb-1 -mx-5 sm:-mx-6 px-5 sm:px-6 ${dim ? "opacity-50" : ""}`}>
      {list.map((app) => (
        <ApplicantBubble
          key={app.id}
          app={app}
          slug={slug}
          requestId={myKiki.id}
          isOpen={openApplicantId === app.id}
          onToggle={() => toggle(app.id)}
          onClose={() => setOpenApplicantId(null)}
        />
      ))}
    </div>
  );

  return (
    <div className="px-5 sm:px-6 py-4 border-b border-black/10 dark:border-white/10">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
          Your kiki
        </span>
        <button
          type="button"
          onClick={onEdit}
          className="text-xs text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
        >
          Edit
        </button>
      </div>

      {apps.length === 0 ? (
        <p className="text-xs text-neutral-400 dark:text-neutral-500 italic">
          No one has reached out yet
        </p>
      ) : (
        <div className="space-y-4">
          {accepted.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                Going · {accepted.length}
              </p>
              {bubbleRow(accepted)}
            </div>
          )}

          {pending.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
                Requests · {pending.length}
              </p>
              {bubbleRow(pending)}
            </div>
          )}

          {declined.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowDeclined((v) => !v)}
                className="flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors"
              >
                {showDeclined ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {declined.length} declined
              </button>
              {showDeclined && (
                <div className="mt-2">
                  {bubbleRow(declined, true)}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ApplicantBubble({
  app,
  slug,
  requestId,
  isOpen,
  onToggle,
  onClose,
}: {
  app: OwnerBuddyApplication;
  slug: string;
  requestId: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const update = useUpdateBuddyApplicationStatus(slug, requestId);
  const toast = useToast();
  const isAccepted = app.status === "accepted";
  const isPending = app.status === "pending";

  const handleClick = () => {
    if (!isOpen && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({
        top: r.bottom + window.scrollY + 8,
        left: Math.max(8, r.left + window.scrollX + r.width / 2 - 112),
      });
    }
    onToggle();
  };

  const setStatus = async (status: "accepted" | "declined" | "pending") => {
    try {
      const res = await update.mutateAsync({ applicationId: app.id, status });
      if (!res.success) toast.error((res as { error?: string }).error || "Couldn't update");
      else onClose();
    } catch (err) {
      toast.error((err as Error).message || "Couldn't update");
    }
  };

  return (
    <div className="shrink-0 flex flex-col items-center gap-1">
      <button
        ref={btnRef}
        type="button"
        onClick={handleClick}
        className="flex flex-col items-center gap-1 focus:outline-none group"
      >
        <div
          className={`ring-2 rounded-full transition-all ${
            isAccepted ? "ring-emerald-500" : "ring-transparent"
          }`}
        >
          <ProfileImage size="sm" user={app.user} className="group-hover:opacity-80 transition-opacity" />
        </div>
        <span className="text-[11px] text-neutral-600 dark:text-neutral-400 font-medium w-12 text-center leading-tight truncate">
          {app.user.firstName}
        </span>
      </button>

      {isOpen && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[998]" onClick={onClose} />
          <div
            style={{ position: "absolute", top: pos.top, left: pos.left, zIndex: 999 }}
            className="w-56 bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-black/10 dark:border-white/10 p-3 space-y-2.5"
          >
            <div className="flex items-center gap-2">
              <ProfileImage size="xs" user={app.user} />
              <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                {formatNameWithInitial(app.user.firstName, app.user.lastName)}
              </span>
              {isAccepted && (
                <span className="ml-auto shrink-0 text-[10px] font-semibold text-emerald-500">Going ✓</span>
              )}
            </div>
            {app.note && (
              <p className="text-xs text-neutral-600 dark:text-neutral-300 leading-relaxed">
                {app.note}
              </p>
            )}
            <div className="flex gap-1.5 pt-0.5">
              {isPending ? (
                <>
                  <button
                    type="button"
                    onClick={() => setStatus("declined")}
                    disabled={update.isPending}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-colors disabled:opacity-50"
                  >
                    <X className="w-3 h-3" /> Decline
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatus("accepted")}
                    disabled={update.isPending}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50"
                  >
                    <Check className="w-3 h-3" /> Accept
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setStatus("pending")}
                  disabled={update.isPending}
                  className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors disabled:opacity-50"
                >
                  Undo
                </button>
              )}
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

// Nearest events by embedding distance in the same city, with other dates of
// THIS event split out into a compact, date-focused row. Renders nothing until
// there's something to show.
function SimilarEvents({ slug }: { slug: string | null }) {
  const { data } = useSimilarEvents(slug);
  const result = data?.success ? data.data : null;
  const similar = result?.similar ?? [];
  const moreDates = result?.moreDates ?? [];
  if (similar.length === 0 && moreDates.length === 0) return null;

  return (
    <>
      {/* Other dates of this same event — date-first, not full cards. */}
      {moreDates.length > 0 && (
        <section className="pt-2">
          <h2 className="text-lg font-semibold tracking-tight mb-3">Other dates</h2>
          <div className="-mx-4 sm:-mx-6 px-4 sm:px-6 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {moreDates.map((ev) => {
              const d = parseUTCDate(ev.date);
              return (
                <Link
                  key={ev.link}
                  href={ev.link}
                  className="shrink-0 flex items-center gap-2.5 pl-2 pr-4 py-2 rounded-xl border border-black/10 dark:border-white/10 hover:border-primary-500 hover:bg-base-200/50 dark:hover:bg-neutral-800/40 transition-colors no-underline"
                >
                  <DateBadge date={ev.date} size="md" />
                  <div className="text-left leading-tight">
                    <div className="text-sm font-medium">{d.weekday}</div>
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">
                      {formatTimeToDisplay(ev.time)}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Genuinely different events — the rail, reusing the events-index behavior. */}
      {similar.length > 0 && (
        <div className="-mx-4 sm:-mx-6 pt-2">
          <CarouselRail
            carousel={{
              id: `similar-${slug ?? ""}`,
              title: "Similar events",
              largeCards: false,
              items: similar,
            }}
          />
        </div>
      )}
    </>
  );
}

function HostCard({ host }: { host: NonNullable<PublicEventResponse["host"]> }) {
  const { group } = host;
  const hostName = host.lastName ? `${host.firstName} ${host.lastName}` : host.firstName;

  if (group) {
    return (
      <Link href={`/g/${group.slug}`} className="flex items-center gap-2 group">
        {group.imageUrl ? (
          <img
            src={group.imageUrl}
            alt={group.name}
            className="w-5 h-5 rounded-full object-cover shrink-0 bg-base-200"
          />
        ) : (
          <div className="w-5 h-5 rounded-full shrink-0 bg-gradient-to-br from-primary-500/60 via-secondary-500/50 to-accent-500/60 flex items-center justify-center">
            <Users className="w-2.5 h-2.5 text-white/80" />
          </div>
        )}
        <p className="text-sm text-neutral-500 dark:text-neutral-400 group-hover:text-primary-500 transition-colors truncate">
          {group.name}
        </p>
      </Link>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <UserCircle2 className="w-4 h-4 text-neutral-400 shrink-0" />
      <span>
        <span className="text-neutral-500 dark:text-neutral-400">Hosted by </span>
        {hostName}
      </span>
    </div>
  );
}
